import { useRef } from "react"
import { Canvas, useFrame, useThree } from "@react-three/fiber"

const shaderMaterial = {
	vertexShader: `
  	mediump float;
  	varying vec2 vUv;

	void main() {
		vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
		vUv = uv;
		gl_Position = projectionMatrix * mvPosition;
	}
  `,
	fragmentShader: `
	uniform float time;
	uniform vec2 mouse;
	uniform vec3 mainColor;
	varying vec2 vUv;

	float smin(float a, float b, float k) {
		float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
		return mix(b, a, h) - k * h * (1.0 - h);
	}

	float sdSphere(vec3 p, float r) {
		return length(p) - r;
	}

	float sdf(vec3 p) {
		float sphere1 = sdSphere(p - vec3(-0.3 + sin(time * 0.03), tan(time * 0.02) + cos(time * 0.2) + sin(time * 0.02), 0.0), 0.4);
		float sphere2 = sdSphere(p - vec3(0.5 * -0.4, tan(time * 0.1), 0.0), 0.3);
		float sphere3 = sdSphere(p - vec3(cos(time * 0.4) + cos(time * 0.04), tan(time * 0.1 + cos(time * 0.02)), 0.0), 0.2);
		float sphere4 = sdSphere(p - vec3(sin(time) * 0.2, tan(time * 0.03) - cos(time + sin(time * 0.03)), 0.0), 0.2);
		float sphere5 = sdSphere(p - vec3(0.4 - cos(time * 0.5), tan(time * 0.04), 0.0), 0.2);

		float min1 = smin(sphere1, sphere2, 0.3);
		float min2 = smin(sphere2, sphere3, 0.3);
		float min3 = smin(sphere3, sphere4, 0.3);
		float min4 = smin(sphere4, sphere5, 0.3);

		float smin1 = smin(min1, min2, 0.3);
		float smin2 = smin(min2, min3, 0.3);
		float smin3 = smin(min3, min4, 0.3);

		float mix1 = smin(smin1, smin2, 0.3);
		float mix2 = smin(smin2, smin3, 0.3);

		return smin(mix1, mix2, 0.3);
	}

	vec3 calcNormal(vec3 p) {
		const vec2 h = vec2(epsilon, 0.0);

		return normalize(vec3(
			sdf(p + h.xyy) - sdf(p - h.xyy),
			sdf(p + h.yxy) - sdf(p - h.yxy),
			sdf(p + h.yyx) - sdf(p - h.yyx)
		));
	}

	void main() {
		vec3 camPos = vec3(0.0, 0.0, 2.0);
		vec3 ray = normalize(vec3(vUv - vec2(0.5), -1.0));
		
		float t = 0.0;
		float tMax = 5.0;

		for (int i = 0; i < 256; i++) {
			vec3 pos = camPos + t * ray;
			float h = sdf(pos);
			
			if (h < epsilon || t > tMax) break;
			t += h;
		}

		vec3 color = vec3(theme);

		vec3 pos = camPos + t * ray;
		vec3 normal = calcNormal(pos);
		float fresnel = pow(1.0 + dot(ray, normal), 1.0);

		if (t < tMax) {
			color = vec3(mainColor * fresnel);	
		}

		gl_FragColor = vec4(color, 1.0);
	}
	`,
}

const Plane = () => {
	const mesh = useRef()
	const { viewport } = useThree()
	const { vertexShader, fragmentShader } = shaderMaterial

	useFrame(({ clock }) => {
		mesh.current.material.uniforms.time.value = clock.getElapsedTime()
	})

	return (
		<mesh ref={mesh}>
			<planeGeometry args={[viewport.width, viewport.height, 1, 1]} />
			<shaderMaterial
				args={[
					{
						extensions: {
							derivatives: "#extension GL_OES_standard_derivatives : enable",
						},
						uniforms: {
							time: { type: "f", value: 0 },
							resolution: { value: { x: 0, y: 0, z: 0, w: 1 } },
							mouse: { type: "vec2", value: { x: 0, y: 0 } },
							mainColor: { type: "vec3", value: { x: 0.0, y: 1.0, z: 1.0 } },
						},
						defines: {
							theme: 1.0,
							epsilon: 0.0001,
						},
						vertexShader,
						fragmentShader,
					},
				]}
			/>
		</mesh>
	)
}

const SDF = () => (
	<Canvas>
		<Plane position={[0, 0, 0]} />
	</Canvas>
)

export default SDF
